import { makeStyles } from '@material-ui/styles'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import Text from 'pf9-ui-components/built/elements/Text'
import type React from 'react'
import ClusterHostGroupsPicklist from 'app/plugins/openstack/components/infrastructure/clusters/ClusterHostGroupsPicklist'
import SshKeyPicklist from 'app/plugins/openstack/components/vms/SshKeyPicklist'
import ExternalLink from 'core/components/ExternalLink'
import SimpleLink from 'core/components/SimpleLink'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import {
  CONTROL_PLANE_TYPES_DOCS_LINK_TEXT,
  CONTROL_PLANE_TYPES_DOCS_URL,
  colocatedControlPlaneCard,
  managedControlPlaneCard,
} from './controlPlaneTypes'
import SelectableCardWrapper from './SelectableCardWrapper'

interface ClusterArchitectureFieldsProps {
  params: any
  getParamsUpdater: (key: string) => (value: unknown) => void
}

/**
 * Cluster Architecture Fields component
 * @param param0 - tenants, params, getParamsUpdater, loading
 * @returns Cluster Architecture Fields component is used for cluster architecture selection for both OpenStack and BYO infrastructure
 * Based on the selected infrastructure in ClusterInfrastructureSelector, the form will be rendered accordingly
 */
export default function ClusterArchitectureFields({
  params,
  getParamsUpdater,
}: ClusterArchitectureFieldsProps) {
  const classes = useStyles()

  return (
    <>
      {/* Select OpenStack Infrastructure */}
      <div className={classes.clusterArchitectureFields}>
        <div className={classes.controlPlaneOptions}>
          <Text variant="body2">Select the type of control plane.</Text>
          <div className={classes.SelectableCardWrappers}>
            <SelectableCardWrapper
              id="managed"
              onClick={getParamsUpdater('controlPlaneType')}
              active={params?.controlPlaneType === 'managed'}
              title="Managed Control Plane"
              description={managedControlPlaneCard.description}
              info={managedControlPlaneCard.info}
            />
            <SelectableCardWrapper
              id="custom"
              onClick={getParamsUpdater('controlPlaneType')}
              active={params?.controlPlaneType === 'custom'}
              title="Colocated Control Plane"
              description={colocatedControlPlaneCard.description}
              info={colocatedControlPlaneCard.info}
            />
          </div>
          <ExternalLink url={CONTROL_PLANE_TYPES_DOCS_URL}>
            {CONTROL_PLANE_TYPES_DOCS_LINK_TEXT}
          </ExternalLink>
        </div>

        <div className={classes.dropdownFields}>
          {/* Select Availability Zone */}
          <DropdownField
            id="openstackCluster"
            label="Virtualized Cluster"
            DropdownComponent={ClusterHostGroupsPicklist}
            value={params?.openstackCluster}
            onChange={getParamsUpdater('openstackCluster')}
            fullWidth
            info="Select the virtualized cluster to which the kubernetes cluster will be attached."
            disableClusterWithoutHosts
            required
          />
          <div className={classes.sshKey}>
            <SimpleLink
              className={classes.createSshKey}
              src={routeManager.routes.openstack.importSshKey.path()}
            >
              Create SSH Key
            </SimpleLink>
            <DropdownField
              id="sshKey"
              includePublicKey
              label="SSH Key"
              DropdownComponent={SshKeyPicklist as React.FC<any>}
              value={params?.sshKey}
              onChange={getParamsUpdater('sshKey')}
              fullWidth
              info="Select an SSH key for your VMs."
              offset={{ vertical: 0, horizontal: -50 }}
              hideNoneOption
              required
            />
          </div>
        </div>

        {/* Commenting this out till Custom Control Plane is supported */}
        {/* <DropdownField
              id="controlPlaneClass"
              label="Control Plane Class"
              DropdownComponent={AsyncDropdown}
              items={controlPlaneClassOptions}
              value={params?.alertMethod}
              onChange={getParamsUpdater('controlPlaneClass')}
              disabled={params?.controlPlaneType === 'managed'}
              selectFirst
            /> */}
      </div>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  clusterArchitectureFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  controlPlaneOptions: {
    display: 'grid',
    margin: theme.spacing(2, 0),
    gridGap: theme.spacing(2),
  },
  sshKey: {
    position: 'relative',
  },
  createSshKey: {
    position: 'absolute',
    right: 0,
    marginTop: theme.spacing(0.5),
    fontSize: '11px',
    zIndex: 100,
  },
  SelectableCardWrappers: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gridGap: theme.spacing(2),
  },
  dropdownFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(2),
    alignItems: 'baseline',
  },
}))
